import { createSlice } from "@reduxjs/toolkit";
import _ from "lodash";
import { fetchOffer, handleDeleteOffer, updateProperties, fetchUserFavourites, handleDuplicateOffer, getOfferDetails, cloneOfferAtConfigurator } from "../services/dashboaardThunks";
import { FILTER_TOOLBAR_TABS, OFFER_STTATUS } from "../utils/consstants";

//
type TcaseAcctions = {
  action: string;
  selectedOffer: {};
};
interface DashboardState {
  data: {
    caseType: string | undefined;
    filterTab: string | undefined;
    viewType: string;
    offerSearch: string | undefined;
    pagination: {};
    caseResults: Offer[];
    favourites: {
      data: Offer[];
      total_count: number;
      isLoading: boolean;
    };
    caseActions: TcaseAcctions | {};
    allCases: {
      data: Offer[];
      total_count: number;
      isLoading: boolean;
    };
    isLoaderDialogOpen: boolean;
    statusCount: {};
    selectedStatus: string;
    duplicateData: { data: Offer[]; };
    parentOfferData: { data: Offer[]; };
    cloneSid: { data: any[] };
  };
  loading: boolean;
  error: string | null;
  users: string[];
  isEditedProperty?: {};
  isEditPropertyLoading: boolean;
  duplicateOfferLoading: boolean;
  clonedOffer: boolean;
}

interface Offer {
  offer_id: string;
  co_owner?: string;
  summary?: string;
  // Add other properties as needed
}
interface EditProperties {
  offer_id: string;
  co_owner: string;
  summary?: string; // Made optional to avoid errors
  assumptions?: string; // Made optional to avoid errors
}

const initialState: DashboardState = {
  data: {
    caseType: undefined,
    filterTab:
      FILTER_TOOLBAR_TABS[0] && FILTER_TOOLBAR_TABS[0].id
        ? FILTER_TOOLBAR_TABS[0].id
        : undefined,
    viewType: "grid",
    offerSearch: undefined,
    pagination: {
      currentPage: 1,
      itemsPerPage: 10,
    },
    caseResults: [] as Offer[],
    // Initialise result dta for all cases
    allCases: {
      data: [],
      total_count: 0,
      isLoading: false,
    },
    favourites: {
      data: [],
      total_count: 0,
      isLoading: false,
    },
    caseActions: {},
    isLoaderDialogOpen: false,
    statusCount: {},
    selectedStatus: "",
    duplicateData: { data: [] },
    parentOfferData: { data: [] },
    cloneSid: { data: [] }
  },
  loading: false,
  error: null,
  users: [],
  isEditedProperty: { edited: false, offerid: '' },
  isEditPropertyLoading: false,
  duplicateOfferLoading: false,
  clonedOffer: false
};

const dashboardSlice = createSlice({
  name: "dashboard",
  initialState,
  reducers: {
    setProperty(state, action) {
      state.data[action.payload.key] = action.payload.value;
    },
    updateBookmark(state, action) {
      if (!state.data.caseResults) {
        state.data.caseResults = [];
      }
      if (!state.data.caseResults) {
        state.data.caseResults = [];
      }
      const index = state.data.caseResults.findIndex(
        (obj) => obj.offer_id === action.payload.offerId
      );
      if (index !== -1 && state.data.caseResults && state.data.caseResults[index]) {
        state.data.caseResults[index]["is_bookmarked"] = action.payload.is_bookmarked;
      }
    },
    updateFavouritesTotalCount(state, action) {
      // Update the total count of favourites in the state
      if (state.data.favourites) {
        state.data.favourites.total_count = action.payload.total_count;
      }
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOffer.pending, (state,action) => {
        state.loading = true;
        state.error = null;
        if (action?.meta?.arg?.caseType === 'all_cases') {
          state.data.allCases.isLoading = true;
        }
      })
      .addCase(fetchOffer.fulfilled, (state, action) => {
        state.loading = false;
        // Update the offer data and offer status count
        // Handle the case where action.meta.arg.caseType is 'all_cases'
        if (action?.meta?.arg?.caseType === 'all_cases') {
          state.data.allCases.data = action.payload?.["offerData"];
          // Get total count of offers for all cases from action.payload?.["statusCount"]
          const statusCount = action.payload?.["statusCount"] || [];
          const totalOffersCount = Object.values(statusCount || {}).reduce((sum: number, count: number) => sum + (Number(count) || 0), 0);
          state.data.allCases.total_count = totalOffersCount as number;
          state.data.allCases.isLoading = false;
        }
        else {
          // For My cases save the offer data and status count
          state.data.caseResults = action.payload?.["offerData"] || [];
          state.data.statusCount = action.payload?.["statusCount"];
        }
      })
      .addCase(updateProperties.pending, (state) => {
        state.isEditPropertyLoading = true;
      })
      .addCase(updateProperties.fulfilled, (state, action) => {
        state.isEditPropertyLoading = false;
        const editPropertiesObj: EditProperties = action.meta.arg;
        const caseResults: Offer = _.find(state.data.caseResults, (item) => item['offer_id'] === editPropertiesObj.offer_id);

        // If the offer is found, update it
        if (caseResults && typeof caseResults === 'object') {
          caseResults['co_owner'] = editPropertiesObj.co_owner;
          caseResults['summary'] = editPropertiesObj.summary;
          caseResults['assumptions'] = editPropertiesObj.assumptions;
        }
      })
      .addCase(handleDeleteOffer.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(handleDeleteOffer.fulfilled, (state, action) => {
        state.loading = false;
        // Access offer_id from action.meta.arg
        const argumentData = action.meta.arg;
        state.data.caseResults = _.filter(state?.data?.caseResults, (item) => item['offer_id'] !== argumentData.offer_id);
        const updatedCount = state?.data?.statusCount?.[state?.data?.selectedStatus] - 1;
        state.data.statusCount[state?.data?.selectedStatus] = updatedCount < 0 ? 0 : updatedCount;
      }).addCase(fetchOffer.rejected, (state, action) => {
        console.log("Error in fetching offer data:", action.payload || action.error);
        state.loading = false;
        state.error = action.payload || "An error occurred while fetching offer data.";
      }).addCase(updateProperties.rejected, (state, action) => {
        console.log("Error in updating properties:", action.payload || action.error);
        state.isEditPropertyLoading = false;
        state.error = action.payload || "An error occurred while updating properties.";
      }).addCase(handleDeleteOffer.rejected, (state, action) => {
        console.log("Error in deleting offer:", action.payload || action.error);
        state.loading = false;
        state.error = action.payload || "An error occurred while deleting the offer.";
      }).addCase(fetchUserFavourites.pending, (state, action) => {
        // Set loading state for fetching user favorites
        state.data.favourites.isLoading = true;
        state.error = null;
      }).
      addCase(fetchUserFavourites.fulfilled, (state, action) => {
        // Update the favourites data
        state.data.favourites.isLoading = false;
        state.data.favourites.data = action.payload?.["data"] || [];
        state.data.favourites.total_count = action.payload?.["total_count"] || 0;
      }).addCase(fetchUserFavourites.rejected, (state, action) => {
        // Handle error while fetching user favourites
        console.log("Error while fetching user favourites:", action.payload || action?.["error"]);
        state.data.favourites.isLoading = false;
        state.error = action.payload || "An error occurred while fetching user favourites.";
      }).addCase(handleDuplicateOffer.pending, (state, action) => {
        // Set loading state for fetching user favorites
        state.duplicateOfferLoading = true;
        state.error = null;
      }).
      addCase(handleDuplicateOffer.fulfilled, (state, action) => {
        // Update the favourites data
        state.duplicateOfferLoading = false;
        state.data.duplicateData.data = action.payload?.["data"] || [];
        if ((state.data?.["selectedStatus"]).toLowerCase() !== 'draft') {
          state.data.selectedStatus = 'draft';
        }
      }).
      addCase(handleDuplicateOffer.rejected, (state, action) => {
        // Handle error while fetching user favourites
        console.log("Error while fetching user favourites:", action.payload || action?.["error"]);
        state.duplicateOfferLoading = false;
        state.error = action.payload || "An error occurred while duplicating offer.";
      }).addCase(getOfferDetails.pending, (state, action) => {
        // Set loading state for fetching user favorites
        state.duplicateOfferLoading = true;
        state.error = null;
      }).
      addCase(getOfferDetails.fulfilled, (state, action) => {
        // Update the favourites data
        // state.duplicateOfferLoading = false;
        state.data.parentOfferData.data = action.payload?.["data"] || [];
      }).
      addCase(getOfferDetails.rejected, (state, action) => {
        // Handle error while fetching user favourites
        console.log("Error while fetching user favourites:", action.payload || action?.["error"]);
        state.duplicateOfferLoading = false;
        state.error = action.payload || "An error occurred while duplicating offer.";
      }).addCase(cloneOfferAtConfigurator.pending, (state, action) => {
        // Set loading state for fetching user favorites
        state.duplicateOfferLoading = true;
        state.error = null;
      }).
      addCase(cloneOfferAtConfigurator.fulfilled, (state, action) => {
        // Update the favourites data
        // state.duplicateOfferLoading = false;
        state.data.cloneSid.data = action.payload?.["data"] || [];
      }).
      addCase(cloneOfferAtConfigurator.rejected, (state, action) => {
        // Handle error while fetching user favourites
        console.log("Error while fetching user favourites:", action.payload || action?.["error"]);
        state.duplicateOfferLoading = false;
        state.error = action.payload || "An error occurred while duplicating offer.";
      })
  },
});

export const { setProperty, updateBookmark, updateFavouritesTotalCount } = dashboardSlice.actions;

export default dashboardSlice.reducer;
